import { createContext, useContext, useState, useRef, useCallback } from 'react';
import Portal from '../components/Portal';

const ActionContext = createContext();

export const useAction = () => {
  const context = useContext(ActionContext);
  if (!context) throw new Error('useAction must be used within an ActionProvider');
  return context;
};

export function ActionProvider({ children }) {
  const [state, setState] = useState({ visible: false, message: '' });
  const depthRef = useRef(0);

  const showAction = useCallback((message = '') => {
    depthRef.current += 1;
    setState({ visible: true, message });
  }, []);

  const hideAction = useCallback(() => {
    depthRef.current = Math.max(0, depthRef.current - 1);
    if (depthRef.current === 0) {
      setState({ visible: false, message: '' });
    }
  }, []);

  const runAction = useCallback(async (asyncFn, options = {}) => {
    const { message = 'Processing...' } = options;
    showAction(message);
    try {
      return await asyncFn();
    } finally {
      hideAction();
    }
  }, [showAction, hideAction]);

  return (
    <ActionContext.Provider value={{ runAction }}>
      {children}
      {state.visible && (
        <Portal>
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-slate-950/50 backdrop-blur-[2px] animate-fade-in">
            <div className="flex flex-col items-center gap-4 p-8 rounded-2xl bg-white/90 dark:bg-slate-800/90 shadow-2xl backdrop-blur-md">
              <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-brand-200 border-t-brand-600 dark:border-slate-600 dark:border-t-brand-400" />
              {state.message && (
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 max-w-64 text-center">
                  {state.message}
                </p>
              )}
            </div>
          </div>
        </Portal>
      )}
    </ActionContext.Provider>
  );
}
